import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/ui/status-pill";
import { ErrorBlock } from "@/features/questionnaire/components/QueryState";

import { useFormationGroupsQuery } from "../api";

export function FormationGroupList() {
  const { t, i18n } = useTranslation();
  const groups = useFormationGroupsQuery();

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  // Without this the table renders headed and empty on a failed fetch, which
  // reads as "there are no training groups" rather than as a failure.
  if (groups.error) return <ErrorBlock error={groups.error} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t("formationGroups.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("formationGroups.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/formation-groups/new">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("formationGroups.create")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.list.name")}
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.enrolment.title")}
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.list.participants")}
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    {t("formationGroups.list.createdAt")}
                  </th>
                  <th className="pb-2 font-medium">
                    {t("formationGroups.list.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(groups.data ?? []).map((group) => (
                  <tr key={group.id} className="border-b border-border">
                    <td className="py-3 pr-4 text-foreground">{group.name}</td>
                    <td className="py-3 pr-4">
                      <StatusPill tone={group.is_open ? "success" : "muted"}>
                        {group.is_open
                          ? t("formationGroups.enrolment.openShort")
                          : t("formationGroups.enrolment.closedShort")}
                      </StatusPill>
                    </td>
                    <td className="py-3 pr-4 text-foreground">
                      {group.participant_count}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {formatDate(group.created_at)}
                    </td>
                    <td className="py-3">
                      <Button asChild variant="outline" size="sm">
                        <Link
                          to="/formation-groups/$id"
                          params={{ id: group.id }}
                        >
                          {t("formationGroups.list.view")}
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
                {groups.data?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-muted-foreground">
                      {t("formationGroups.list.empty")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
